// /brokers/reconciliation — broker reconciliation oversight (SM-716,
// SM-621 phase 08a).
//
// Three sources, each broker-service's or ops-api's own, never
// recomputed here:
//
//   Funding    the latest daily funding reconciliation run (SM-692 §7):
//              equation E1 per (root, currency) — top-ups − completed
//              payouts against node balances + user allocations − user
//              returns — and every broken rule (E1…E6) it found.
//              BROKER_VIEW.
//   Revenue    the revenue reconciliation runs (SM-711): ledger pending /
//              available against the earnings registry per (broker,
//              currency), and escrow against open payout batches; a run
//              opens to its comparisons. BROKER_REVENUE_VIEW.
//   Ops        open ops-platform findings of the broker task types
//              (broker — SM-724's core reconciliation — exposure,
//              clearing), with a link into the ops console. OPS_VIEW.
//
// Discrepancy cases (SM-724, phase 08d) are those findings: each one
// preserves broker, root, account, currency, source and operation, is
// worked through its approved action (a deep link to the reviewed
// operation, never a balance edit) and closed with notes on the finding
// page. The integrity dashboards are /ops/brokers.
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  fundingReconLabel,
  fundingReconTone,
  revenueReconLabel,
  revenueReconTone,
} from "@/components/brokers/governance";
import { canonToWire, msToIso } from "@/components/brokers/receivables";
import { Badge } from "@/components/ui/Badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { CurrencyAmount, CurrencyBadge } from "@/components/ui/CurrencyAmount";
import { EmptyState } from "@/components/ui/EmptyState";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { Pagination } from "@/components/ui/Pagination";
import { Skeleton } from "@/components/ui/Skeleton";
import { useMyPerms } from "@/hooks/useMyPerms";
import {
  brokerKeys,
  fetchFundingReconLatest,
  fetchRevenueReconRun,
  fetchRevenueReconRuns,
} from "@/lib/broker-queries";
import type { FundingReconRunItem, RevenueReconRunItem } from "@/lib/broker-types";
import { cn } from "@/lib/cn";
import { fmtDate } from "@/lib/format";
import { fetchFindings, opsKeys } from "@/lib/ops-queries";
import { severityLabel, type TaskType } from "@/lib/ops-types";
import { Perm } from "@/lib/perms";

const RUNS_PAGE = 10;

/** The ops task types that reconcile broker state. */
const BROKER_TASK_TYPES: TaskType[] = ["broker", "exposure", "clearing"];

interface Search {
  /** The revenue run whose comparisons are open. */
  run?: string;
  page?: number;
}

export const Route = createFileRoute("/_app/brokers/reconciliation")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    run: typeof s.run === "string" && s.run.trim() ? s.run.trim() : undefined,
    page: Number.isFinite(Number(s.page)) && Number(s.page) > 0 ? Number(s.page) : undefined,
  }),
  component: BrokerReconciliationPage,
});

const th = "px-3 py-2.5 text-left text-2xs font-medium uppercase tracking-wide text-muted";
const td = "px-3 py-2.5 text-xs";

function BrokerReconciliationPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const perms = useMyPerms();
  const canFunding = perms.has(Perm.BrokerView);
  const canRevenue = perms.has(Perm.BrokerRevenueView);
  const canOps = perms.has(Perm.OpsView);

  if (perms.loaded && !canFunding && !canRevenue) {
    return (
      <EmptyState
        title="No access"
        description="You need BROKER_VIEW or BROKER_REVENUE_VIEW to see broker reconciliation."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Broker reconciliation</h1>
        <p className="text-sm text-muted">
          What broker-service&apos;s own reconciliations found: funding balances against the ledger, and revenue against
          the earnings registry. Figures are per currency, exactly as each run recorded them.
        </p>
      </div>

      {canFunding ? <FundingReconCard /> : null}
      {canRevenue ? (
        <RevenueReconCard
          page={search.page ?? 0}
          openRun={search.run}
          onPage={(p) => void navigate({ search: (s) => ({ ...s, page: p > 0 ? p : undefined }) })}
          onOpen={(id) => void navigate({ search: (s) => ({ ...s, run: id }) })}
        />
      ) : null}
      {canOps ? <OpsFindingsCard /> : null}

      {canOps ? (
        <Card>
          <CardHeader>
            <CardTitle>Discrepancy cases</CardTitle>
          </CardHeader>
          <CardBody className="space-y-2 text-sm">
            <p>
              Every discrepancy the core broker reconciliation finds is a case: it names the root, broker, account,
              currency, source and operation, links to its approved action — a reviewed operation, never a balance
              edit — and closes with notes.
            </p>
            <div className="flex flex-wrap gap-4 text-xs">
              <Link to="/ops/findings" search={{ task_type: "broker", only_open: "true" }} className="text-neon hover:underline">
                Open broker cases
              </Link>
              <Link to="/ops/brokers" className="text-neon hover:underline">
                Broker integrity dashboards
              </Link>
            </div>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}

function FundingReconCard() {
  const q = useQuery({ queryKey: brokerKeys.fundingRecon(), queryFn: fetchFundingReconLatest });
  const run = q.data;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Funding reconciliation</CardTitle>
        {run ? <Badge tone={fundingReconTone(run.status)}>{fundingReconLabel(run.status)}</Badge> : null}
      </CardHeader>
      <CardBody className="space-y-4">
        {q.isLoading ? <Skeleton className="h-24" /> : null}
        {q.isError ? <ErrorPanel error={q.error} reset={() => void q.refetch()} /> : null}
        {q.data === null ? <p className="text-sm text-muted">No funding reconciliation has run yet.</p> : null}
        {run ? <FundingRun run={run} /> : null}
      </CardBody>
    </Card>
  );
}

function FundingRun({ run }: { run: FundingReconRunItem }) {
  return (
    <>
      <dl className="grid gap-x-8 gap-y-1.5 text-xs sm:grid-cols-2 lg:grid-cols-4">
        <Pair label="Run" value={<span className="font-mono text-2xs">{run.run_id}</span>} />
        <Pair label="Trigger" value={run.trigger} />
        <Pair label="Started" value={fmtDate(msToIso(run.started_at_unix_ms))} />
        <Pair label="Finished" value={run.finished_at_unix_ms ? fmtDate(msToIso(run.finished_at_unix_ms)) : "—"} />
      </dl>
      {run.error ? (
        <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-2xs text-danger">{run.error}</p>
      ) : null}
      {run.status === "FAILED" ? (
        <p className="text-2xs text-danger">
          One of its sources could not be read, so this run judged nothing. It is never reported as balanced.
        </p>
      ) : null}

      <div className="space-y-2">
        <h4 className="text-2xs font-semibold uppercase tracking-wide text-muted">Per root and currency (E1)</h4>
        {run.roots.length === 0 ? (
          <p className="text-2xs text-muted">The run covered no root.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-surface-2">
                <tr>
                  <th className={th}>Root</th>
                  <th className={th}>Currency</th>
                  <th className={`${th} text-right`}>Top-ups</th>
                  <th className={`${th} text-right`}>Payouts completed</th>
                  <th className={`${th} text-right`}>Payouts open</th>
                  <th className={`${th} text-right`}>Node balances</th>
                  <th className={`${th} text-right`}>User allocations</th>
                  <th className={`${th} text-right`}>User returns</th>
                  <th className={`${th} text-right`}>Nodes</th>
                  <th className={th}>Result</th>
                </tr>
              </thead>
              <tbody>
                {run.roots.map((r) => (
                  <tr key={`${r.root_broker_id}-${r.currency}`} className={cn("border-b border-border/60 last:border-0", !r.balanced && "bg-warning/5")}>
                    <td className={td}>
                      <Link to="/brokers/$id" params={{ id: r.root_broker_id }} className="font-mono text-2xs hover:underline">
                        {r.root_broker_id}
                      </Link>
                    </td>
                    <td className={td}>
                      <CurrencyBadge currency={r.currency} />
                    </td>
                    <td className={`${td} text-right`}>
                      <CurrencyAmount currency={r.currency} value={canonToWire(r.top_ups)} />
                    </td>
                    <td className={`${td} text-right`}>
                      <CurrencyAmount currency={r.currency} value={canonToWire(r.payouts_completed)} />
                    </td>
                    <td className={`${td} text-right`}>
                      <CurrencyAmount currency={r.currency} value={canonToWire(r.payouts_open)} tone="muted" />
                    </td>
                    <td className={`${td} text-right`}>
                      <CurrencyAmount currency={r.currency} value={canonToWire(r.node_total)} />
                    </td>
                    <td className={`${td} text-right`}>
                      <CurrencyAmount currency={r.currency} value={canonToWire(r.user_allocations)} />
                    </td>
                    <td className={`${td} text-right`}>
                      <CurrencyAmount currency={r.currency} value={canonToWire(r.user_returns)} />
                    </td>
                    <td className={`${td} text-right font-mono`}>{r.nodes}</td>
                    <td className={td}>
                      {r.balanced ? <Badge tone="success">Balanced</Badge> : <Badge tone="warning">Not balanced</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <h4 className="text-2xs font-semibold uppercase tracking-wide text-muted">Findings</h4>
        {run.findings.length === 0 ? (
          <p className="text-2xs text-muted">No rule was broken.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-surface-2">
                <tr>
                  <th className={th}>Rule</th>
                  <th className={th}>Severity</th>
                  <th className={th}>Broker</th>
                  <th className={th}>Currency</th>
                  <th className={`${th} text-right`}>Expected</th>
                  <th className={`${th} text-right`}>Actual</th>
                  <th className={th}>Detail</th>
                </tr>
              </thead>
              <tbody>
                {run.findings.map((f, i) => (
                  <tr key={`${f.rule}-${f.broker_id ?? f.root_broker_id ?? ""}-${f.currency ?? ""}-${i}`} className="border-b border-border/60 align-top last:border-0">
                    <td className={`${td} font-mono`}>{f.rule}</td>
                    <td className={td}>
                      <Badge tone={f.severity === "CRITICAL" || f.severity === "ERROR" ? "danger" : "warning"}>{f.severity}</Badge>
                    </td>
                    <td className={td}>
                      {f.broker_id || f.root_broker_id ? (
                        <Link
                          to="/brokers/$id"
                          params={{ id: (f.broker_id || f.root_broker_id) as string }}
                          className="font-mono text-2xs hover:underline"
                        >
                          {f.broker_id || f.root_broker_id}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className={td}>{f.currency ? <CurrencyBadge currency={f.currency} /> : "—"}</td>
                    <td className={`${td} text-right`}>
                      {f.currency && f.expected ? <CurrencyAmount currency={f.currency} value={canonToWire(f.expected)} /> : f.expected || "—"}
                    </td>
                    <td className={`${td} text-right`}>
                      {f.currency && f.actual ? <CurrencyAmount currency={f.currency} value={canonToWire(f.actual)} /> : f.actual || "—"}
                    </td>
                    <td className={`${td} text-2xs`}>
                      <div>{f.detail}</div>
                      {f.operation_id ? <div className="font-mono text-muted">{f.operation_id}</div> : null}
                      {f.transfer_id ? <div className="font-mono text-muted">{f.transfer_id}</div> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function RevenueReconCard({
  page,
  openRun,
  onPage,
  onOpen,
}: {
  page: number;
  openRun?: string;
  onPage: (p: number) => void;
  onOpen: (runID: string | undefined) => void;
}) {
  const filter = { limit: RUNS_PAGE, offset: page * RUNS_PAGE };
  const q = useQuery({
    queryKey: brokerKeys.revenueReconRuns(filter),
    queryFn: () => fetchRevenueReconRuns(filter),
    placeholderData: keepPreviousData,
  });
  const runs = q.data?.items ?? [];
  const selected = openRun ?? runs[0]?.run_id;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Revenue reconciliation</CardTitle>
        <span className="text-2xs text-muted">{q.data?.page.total ?? 0}</span>
      </CardHeader>
      <CardBody className="p-0">
        {q.isLoading ? (
          <div className="p-5">
            <Skeleton className="h-16" />
          </div>
        ) : null}
        {q.isError ? <ErrorPanel error={q.error} reset={() => void q.refetch()} /> : null}
        {q.data && runs.length === 0 ? (
          <p className="px-5 py-10 text-center text-sm text-muted">No revenue reconciliation has run yet.</p>
        ) : null}
        {runs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-surface-2">
                <tr>
                  <th className={`${th} px-5`}>Run</th>
                  <th className={th}>Result</th>
                  <th className={th}>As of</th>
                  <th className={th}>Triggered by</th>
                  <th className={`${th} text-right`}>Mismatches</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr
                    key={r.run_id}
                    className={cn(
                      "cursor-pointer border-b border-border/60 last:border-0 hover:bg-surface-2/60",
                      r.run_id === selected && "bg-surface-2",
                    )}
                    onClick={() => onOpen(r.run_id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") onOpen(r.run_id);
                    }}
                    tabIndex={0}
                    aria-selected={r.run_id === selected}
                  >
                    <td className={`${td} px-5 font-mono text-2xs`}>{r.run_id}</td>
                    <td className={td}>
                      <Badge tone={revenueReconTone(r.state)}>{revenueReconLabel(r.state)}</Badge>
                    </td>
                    <td className={`${td} text-2xs text-muted`}>{fmtDate(msToIso(r.as_of_unix_ms))}</td>
                    <td className={`${td} font-mono text-2xs text-muted`}>{r.triggered_by}</td>
                    <td className={cn(`${td} text-right font-mono`, r.mismatches > 0 && "text-warning")}>{r.mismatches}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {q.data && q.data.page.total > RUNS_PAGE ? (
          <Pagination page={q.data.page} currentPage={page} onPageChange={onPage} itemLabel="entries" />
        ) : null}
        {selected ? <RevenueRunDetail runID={selected} /> : null}
      </CardBody>
    </Card>
  );
}

function RevenueRunDetail({ runID }: { runID: string }) {
  const q = useQuery({
    queryKey: brokerKeys.revenueReconRun(runID),
    queryFn: () => fetchRevenueReconRun(runID),
  });
  const run: RevenueReconRunItem | undefined = q.data;
  return (
    <div className="space-y-3 border-t border-border px-5 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-2xs font-semibold uppercase tracking-wide text-muted">Run detail</h4>
        <span className="font-mono text-2xs text-muted">{runID}</span>
      </div>
      {q.isLoading ? <Skeleton className="h-16" /> : null}
      {q.isError ? <ErrorPanel error={q.error} reset={() => void q.refetch()} /> : null}
      {run?.error ? (
        <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-2xs text-danger">{run.error}</p>
      ) : null}
      {run ? (
        <>
          {(run.items ?? []).length === 0 ? (
            <p className="text-2xs text-muted">The run compared no broker balance.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={th}>Broker</th>
                    <th className={th}>Currency</th>
                    <th className={`${th} text-right`}>Ledger pending</th>
                    <th className={`${th} text-right`}>Earnings pending</th>
                    <th className={`${th} text-right`}>Ledger available</th>
                    <th className={`${th} text-right`}>Earnings available</th>
                    <th className={th}>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {(run.items ?? []).map((it) => (
                    <tr key={`${it.broker_id}-${it.currency}`} className={cn("border-b border-border/60 last:border-0", !it.matched && !it.in_flight && "bg-warning/5")}>
                      <td className={td}>
                        <Link to="/brokers/$id" params={{ id: it.broker_id }} className="font-mono text-2xs hover:underline">
                          {it.broker_id}
                        </Link>
                      </td>
                      <td className={td}>
                        <CurrencyBadge currency={it.currency} />
                      </td>
                      <td className={`${td} text-right`}>
                        <CurrencyAmount currency={it.currency} value={canonToWire(it.ledger_pending)} />
                      </td>
                      <td className={`${td} text-right`}>
                        <CurrencyAmount currency={it.currency} value={canonToWire(it.earnings_pending)} />
                      </td>
                      <td className={`${td} text-right`}>
                        <CurrencyAmount currency={it.currency} value={canonToWire(it.ledger_available)} />
                      </td>
                      <td className={`${td} text-right`}>
                        <CurrencyAmount currency={it.currency} value={canonToWire(it.earnings_available)} />
                      </td>
                      <td className={td}>
                        {it.in_flight ? (
                          <Badge tone="accent">In flight, not judged</Badge>
                        ) : it.matched ? (
                          <Badge tone="success">Matched</Badge>
                        ) : (
                          <Badge tone="warning">Mismatch</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {(run.escrow ?? []).length > 0 ? (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={th}>Payout escrow</th>
                    <th className={`${th} text-right`}>Ledger escrow</th>
                    <th className={`${th} text-right`}>Open batches</th>
                    <th className={th}>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {(run.escrow ?? []).map((e) => (
                    <tr key={e.currency} className="border-b border-border/60 last:border-0">
                      <td className={td}>
                        <CurrencyBadge currency={e.currency} />
                      </td>
                      <td className={`${td} text-right`}>
                        <CurrencyAmount currency={e.currency} value={canonToWire(e.ledger_escrow)} />
                      </td>
                      <td className={`${td} text-right`}>
                        <CurrencyAmount currency={e.currency} value={canonToWire(e.batches_reserved)} />
                      </td>
                      <td className={td}>
                        {e.in_flight ? (
                          <Badge tone="accent">In flight, not judged</Badge>
                        ) : e.matched ? (
                          <Badge tone="success">Matched</Badge>
                        ) : (
                          <Badge tone="warning">Mismatch</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function OpsFindingsCard() {
  const params = { task_type: BROKER_TASK_TYPES, only_open: true, limit: 20 };
  const q = useQuery({ queryKey: opsKeys.findings(params), queryFn: () => fetchFindings(params) });
  const items = q.data?.Items ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Open ops findings on brokers</CardTitle>
        <div className="flex gap-3 text-2xs">
          <Link to="/ops/findings" search={{ task_type: "exposure" }} className="text-neon hover:underline">
            Exposure findings
          </Link>
          <Link to="/ops/findings" search={{ task_type: "clearing" }} className="text-neon hover:underline">
            Clearing findings
          </Link>
          <Link to="/ops/findings" search={{ task_type: "broker" }} className="text-neon hover:underline">
            Broker findings
          </Link>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        {q.isLoading ? (
          <div className="p-5">
            <Skeleton className="h-12" />
          </div>
        ) : null}
        {q.isError ? <ErrorPanel error={q.error} reset={() => void q.refetch()} /> : null}
        {q.data && items.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-muted">No open broker, exposure or clearing finding.</p>
        ) : null}
        {items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-surface-2">
                <tr>
                  <th className={`${th} px-5`}>Finding</th>
                  <th className={th}>Task</th>
                  <th className={th}>Severity</th>
                  <th className={th}>Entity</th>
                  <th className={th}>Last detected</th>
                  <th className={`${th} text-right`}>Seen</th>
                </tr>
              </thead>
              <tbody>
                {items.map((f) => (
                  <tr key={f.finding_id} className="border-b border-border/60 last:border-0">
                    <td className={`${td} px-5`}>
                      <Link
                        to="/ops/findings/$id"
                        params={{ id: f.finding_id }}
                        className="font-mono text-2xs hover:underline"
                      >
                        {f.finding_code}
                      </Link>
                    </td>
                    <td className={`${td} font-mono text-2xs`}>{f.task_type}</td>
                    <td className={td}>
                      <Badge tone={f.severity === 1 ? "danger" : f.severity === 2 ? "warning" : "muted"}>{severityLabel(f.severity)}</Badge>
                    </td>
                    <td className={`${td} font-mono text-2xs`}>
                      {f.entity_type} {f.entity_id}
                    </td>
                    <td className={`${td} text-2xs text-muted`}>{fmtDate(msToIso(f.last_detected_at_unix_ms))}</td>
                    <td className={`${td} text-right font-mono`}>{f.detected_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function Pair({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 sm:block">
      <dt className="text-2xs text-muted">{label}</dt>
      <dd className="text-xs">{value}</dd>
    </div>
  );
}
